import { CaretDown, SignOut, UserCircle } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../lib/auth/AuthContext";

export function AccountMenu() {
  const { loading, session, profile, needsProfile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    function closeOnOutsidePointer(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  async function handleSignOut() {
    await signOut();
    window.location.assign("/");
  }

  const triggerLabel = loading
    ? "Loading account"
    : profile
      ? `Account menu for ${profile.username}`
      : session
        ? "Finish account profile"
        : "Guest account menu";

  return (
    <div className="account-menu" ref={menuRef}>
      <button
        type="button"
        className="profile-button"
        aria-label={triggerLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <UserCircle size={24} weight="duotone" aria-hidden="true" />
        <CaretDown size={12} weight="bold" aria-hidden="true" />
      </button>

      {open ? (
        <div className="account-dropdown" role="dialog" aria-label="Account menu">
          <div className="account-dropdown-head">
            <span>{profile ? `@${profile.username}` : session ? "Profile required" : "Guest"}</span>
            <strong>{profile ? `${profile.points} contribution points` : "Local use stays available"}</strong>
          </div>

          <nav aria-label="Account links">
            {profile ? <a href={`/${profile.username}`}>Profile</a> : null}
            {needsProfile ? <a href="/register">Finish profile</a> : null}
            {session ? <a href="/settings">Settings</a> : <a href="/login">Sign in to sync</a>}
            <a href="/">Saved spots</a>
            <a href="/contact">Help / Support</a>
            {profile?.role === "admin" ? <a href="/admin">Admin</a> : null}
          </nav>

          {session ? (
            <button type="button" className="account-sign-out" onClick={() => void handleSignOut()}>
              <SignOut size={17} weight="duotone" aria-hidden="true" />
              Log out
            </button>
          ) : (
            <a className="account-create-link" href="/register">Create account</a>
          )}
        </div>
      ) : null}
    </div>
  );
}
